/**
 * ProductMockup v8 — 360° photo turntable + canvas design overlay on print faces.
 */
(function (global) {
  'use strict';

  const SPIN_DEFAULT = ['000', '045', '090', '135', '180', '225', '270', '315'];

  const COLOR_FILTERS = {
    white: 'none',
    black: 'brightness(0.62) saturate(0.42) contrast(1.12)',
    navy: 'brightness(0.72) saturate(1.05) hue-rotate(198deg) contrast(1.05)',
    gray: 'brightness(0.88) saturate(0.22) contrast(0.96)',
    natural: 'brightness(1.02) saturate(0.82) sepia(0.12)',
    clear: 'brightness(1.04) saturate(0.75)',
  };

  function norm(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function resolveColorKey(productCfg, colorLabel) {
    const n = norm(colorLabel);
    if (!productCfg?.colors) return productCfg?.defaultColor || 'white';
    for (const [key, meta] of Object.entries(productCfg.colors)) {
      if (norm(key) === n) return key;
      if ((meta.aliases || []).some((a) => norm(a) === n || n.includes(norm(a)))) return key;
    }
    return productCfg.defaultColor || Object.keys(productCfg.colors)[0];
  }

  function waitImg(img) {
    if (img.complete && img.naturalWidth) return Promise.resolve(img);
    return new Promise((resolve, reject) => {
      img.addEventListener('load', () => resolve(img), { once: true });
      img.addEventListener('error', () => reject(new Error('photo fail')), { once: true });
    });
  }

  function aliasView(view) {
    if (view === 'front') return '000';
    if (view === 'back') return '180';
    if (view === 'angle-left') return '045';
    if (view === 'angle-right') return '315';
    return view;
  }

  class ProductMockup {
    constructor(canvas, photoEl, config, options) {
      this.canvas = canvas;
      this.photoEl = photoEl;
      this.ctx = canvas.getContext('2d');
      this.config = config || { products: {} };
      this.photos = (options && options.photos) || { products: {}, thumbs: {} };
      this.productKey = 'camiseta';
      this.view = '000';
      this.colorKey = 'white';
      this.design = null;
      this.transform = { x: 0, y: 0, scale: 1 };
      this.pulse = 0;
      this._drawRaf = 0;
      this._baseImg = null;
      this._preload = {};
    }

    productCfg() {
      return this.config.products[this.productKey] || this.config.products.camiseta;
    }

    spinViews() {
      const cfg = this.productCfg();
      return (cfg && cfg.views) || this.config.spinAngles || SPIN_DEFAULT;
    }

    canPrintOnView(view) {
      const cfg = this.productCfg();
      const v = aliasView(view || this.view);
      const list = cfg.printViews || ['000', '180', 'front', 'back'];
      return list.includes(v) || list.includes(view);
    }

    photoUrl(view) {
      const views = this.photos.products[this.productKey] || {};
      const v = aliasView(view || this.view);
      return views[v] || views[view] || views.front || views['000'] || views.back || views['180'] || '';
    }

    applyColorFilter() {
      if (!this.photoEl) return;
      this.photoEl.style.filter = COLOR_FILTERS[this.colorKey] || 'none';
    }

    async setProduct(key, opts) {
      opts = opts || {};
      this.productKey = key;
      const cfg = this.productCfg();
      const rawView = opts.view || cfg.defaultView || this.spinViews()[0] || '000';
      this.view = aliasView(rawView);
      this.colorKey = resolveColorKey(cfg, opts.color || cfg.defaultColor);
      await this._loadPhoto(this.view);
      this.scheduleRender();
      this.prefetch();
    }

    async setFrame(view) {
      this.view = aliasView(view);
      await this._loadPhoto(this.view);
      this.scheduleRender();
    }

    async _loadPhoto(view) {
      const url = this.photoUrl(view);
      if (!this.photoEl || !url) return;
      if (this.photoEl.src === url && this.photoEl.complete) {
        this._baseImg = this.photoEl;
        this.applyColorFilter();
        return;
      }
      this.photoEl.hidden = false;
      this.photoEl.src = url;
      this.applyColorFilter();
      try {
        await waitImg(this.photoEl);
        this._baseImg = this.photoEl;
      } catch (_) {
        this._baseImg = null;
      }
    }

    setDesign(image, transform) {
      this.design = image || null;
      if (transform) this.transform = { ...this.transform, ...transform };
      this.scheduleRender();
    }

    setPulse(amount) {
      this.pulse = amount || 0;
      this.scheduleRender();
    }

    setRotateT() {}

    fitDesignToPrintArea() {
      const cfg = this.productCfg();
      const def = cfg.designDefaults || { scale: 0.92 };
      this.transform = { x: 0, y: 0, scale: def.scale || 0.92 };
      this.scheduleRender();
      return this.transform;
    }

    getQualityLabel() {
      if (!this.design) return null;
      const cfg = this.productCfg();
      const px = Math.min(this.design.naturalWidth || 0, this.design.naturalHeight || 0);
      if (px >= (cfg.excellentPrintPx || 2400)) return { key: 'excellent', label: 'Calidad de imagen: Excelente' };
      if (px >= (cfg.goodPrintPx || 1800)) return { key: 'good', label: 'Calidad de imagen: Buena' };
      if (px >= (cfg.minPrintPx || 1000)) return { key: 'ok', label: 'Calidad de imagen: Aceptable' };
      return { key: 'low', label: 'Calidad de imagen: Baja' };
    }

    printArea() {
      if (!this.canPrintOnView(this.view)) return null;
      const cfg = this.productCfg();
      const areas = cfg.printArea || {};
      const v = this.view;
      return areas[v] || areas.front || areas.back || areas['000'] || areas['180'] || null;
    }

    sizeCanvas() {
      const stage = this.canvas.parentElement;
      const rect = stage ? stage.getBoundingClientRect() : { width: 900, height: 900 };
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w, h };
    }

    printBox(w, h) {
      const a = this.printArea();
      if (!a) return null;
      return { x: a.x * w, y: a.y * h, w: a.w * w, h: a.h * h, radius: (a.radius || 0.02) * Math.min(w, h) };
    }

    scheduleRender() {
      if (this._drawRaf) return;
      this._drawRaf = requestAnimationFrame(() => {
        this._drawRaf = 0;
        this.render();
      });
    }

    render() {
      const ctx = this.ctx;
      const { w, h } = this.sizeCanvas();
      ctx.clearRect(0, 0, w, h);

      const wrap = this.canvas.closest('[data-pz-mockup-host]');
      if (wrap) wrap.classList.add('is-light-studio');

      if (!this.design || !this.canPrintOnView(this.view)) return;

      const box = this.printBox(w, h);
      if (!box) return;

      const cfg = this.productCfg();
      const profile = cfg.renderProfile || 'apparel';
      const blend = cfg.blendMode || 'multiply';
      const t = this.transform;
      const cx = box.x + box.w / 2 + t.x;
      const cy = box.y + box.h / 2 + t.y;
      const base = Math.min(box.w / this.design.naturalWidth, box.h / this.design.naturalHeight);
      const s = base * (t.scale || 1);
      const dw = this.design.naturalWidth * s;
      const dh = this.design.naturalHeight * s;

      ctx.save();
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(box.x, box.y, box.w, box.h, box.radius);
      } else {
        ctx.rect(box.x, box.y, box.w, box.h);
      }
      ctx.clip();

      ctx.save();
      ctx.translate(cx, cy);
      if (profile === 'apparel') ctx.transform(1, 0.015, -0.01, 1, 0, 0);
      if (this.pulse) ctx.filter = `brightness(${1 + this.pulse * 0.15})`;

      ctx.globalCompositeOperation = blend;
      ctx.globalAlpha = 0.9;
      ctx.drawImage(this.design, -dw / 2, -dh / 2, dw, dh);

      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = '#3d3428';
      ctx.fillRect(-dw / 2, -dh / 2, dw, dh);

      ctx.restore();
      ctx.restore();
    }

    exportPreview(maxPx) {
      maxPx = maxPx || 600;
      const stage = this.canvas.parentElement;
      const rect = stage ? stage.getBoundingClientRect() : { width: 600, height: 600 };
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.min(w, maxPx));
      c.height = Math.max(1, Math.min(h, maxPx));
      const cx = c.getContext('2d');
      if (this.photoEl && this.photoEl.naturalWidth) {
        cx.drawImage(this.photoEl, 0, 0, c.width, c.height);
      }
      cx.drawImage(this.canvas, 0, 0, c.width, c.height);
      try {
        return c.toDataURL('image/jpeg', 0.88);
      } catch (_) {
        return null;
      }
    }

    getThumbUrl(productKey) {
      return this.photos.thumbs?.[productKey] || null;
    }

    async prefetch() {
      // Eager-load only printable faces; mid-spin frames load on demand.
      const eager = (this.productCfg()?.printViews || ['000', '180']).filter(Boolean);
      eager.forEach((v) => {
        const url = this.photoUrl(v);
        if (!url || this._preload[url]) return;
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        this._preload[url] = img;
      });
    }
  }

  global.STPersonalizaMockup = { ProductMockup, COLOR_FILTERS, SPIN_DEFAULT, aliasView };
})(typeof window !== 'undefined' ? window : globalThis);
